import { create } from 'zustand';

import type { AnalysisT } from '@/features/Analysis/types';
import type { DashboardDateFilterState } from '@/features/Dashboard/components/DashboardDateFilter';
import type { Dashboard2T, FeedbacksSettingT } from '@/features/Dashboard/types';
import { arrayToMap } from '@/utils/util';

type PendingFeedbackGalleryT = {
  dashboardId: string;
  rowIndex: number;
  feedbacksSetting: FeedbacksSettingT;
};

type State = {
  currDashboard: Dashboard2T | null;
  textAreaState: TextAreaState;
  analysisMap: Record<string, AnalysisT>;
  analysisRequestTimeMap: Record<string, number>; // Track analysis request start times
  editingState: boolean;
  globalFilter: StructuredFilter | null;
  globalDateFilter: DashboardDateFilterState | null;
  pendingFeedbackGallery: PendingFeedbackGalleryT | null;
};

type TextAreaState = {
  status: boolean;
  text: string;
};

type Actions = {
  setState: StoreSetter<State>;
  setCurrDashboard: (dashboard: Dashboard2T) => void;
  clearCurrDashboard: () => void;
  setTextArea: (textAreaState: TextAreaState) => void;
  initDashboard2Store: () => void;
  setAnalysisItem: (alias: string, analysis: AnalysisT) => void;
  setAnalysisMap: (analyses: AnalysisT[]) => void;
  setAnalysisRequestTime: (alias: string, startTime: number) => void;
  getAnalysisRequestTime: (alias: string) => number | undefined;
  setEditingState: (editingState: boolean) => void;
  clearEditingState: () => void;
  setGlobalFilter: (globalFilter: StructuredFilter | null) => void;
  setGlobalDateFilter: (
    globalDateFilter: DashboardDateFilterState | null,
  ) => void;
  setPendingFeedbackGallery: (pending: PendingFeedbackGalleryT | null) => void;
};

const initialState: State = {
  currDashboard: null,
  textAreaState: {
    status: false,
    text: '',
  },
  analysisMap: {},
  analysisRequestTimeMap: {},
  editingState: false,
  globalFilter: null,
  globalDateFilter: null,
  pendingFeedbackGallery: null,
};

const useDashboard2Store = create<State & Actions>((set, get) => ({
  ...initialState,
  setGlobalDateFilter(globalDateFilter) {
    set({
      globalDateFilter,
    });
  },
  setGlobalFilter(globalFilter) {
    set({
      globalFilter,
    });
  },
  setTextArea(textAreaState) {
    set({
      textAreaState,
    });
  },

  setAnalysisItem(alias, analysis) {
    set({
      analysisMap: { ...get().analysisMap, [alias]: analysis },
    });
  },
  setAnalysisMap(analyses) {
    const map = arrayToMap(analyses, 'alias');
    set({
      analysisMap: map,
    });
  },
  setAnalysisRequestTime(alias, startTime) {
    set({
      analysisRequestTimeMap: {
        ...get().analysisRequestTimeMap,
        [alias]: startTime,
      },
    });
  },
  getAnalysisRequestTime(alias) {
    return get().analysisRequestTimeMap[alias];
  },
  setCurrDashboard(dashboard) {
    set({
      currDashboard: dashboard,
    });
  },
  clearCurrDashboard() {
    set({
      currDashboard: null,
    });
  },
  setEditingState(editingState) {
    set({
      editingState: editingState,
    });
  },
  clearEditingState() {
    set({
      editingState: false,
    });
  },
  setState: set,
  setPendingFeedbackGallery(pending) {
    set({ pendingFeedbackGallery: pending });
  },
  initDashboard2Store: () => set(initialState),
}));

export default useDashboard2Store;
